import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { currencyLabel, formatReportCurrency, reportCurrencyNumberFormat } from './reportCurrency';

export const PERIOD_LABELS = Object.freeze({ daily: 'Today', weekly: 'This week', monthly: 'This month' });
export const METRIC_DEFINITIONS = Object.freeze({
  'Net receipts': 'Paid receipts less refunds within the reporting period.',
  'Gross item sales': 'Snapshot item sales for orders paid within the reporting period, before order-level refunds/adjustments.',
});
const nonempty = value => typeof value === 'string' && value.trim().length > 0;
const numeric = value => typeof value === 'number' && Number.isFinite(value);

// Validate the actual API contract before accepting or exporting a snapshot.
// Never coerce missing financial values to zero or guess currency.
export function validateReport(report, restaurantId = report?.restaurant?.id, period = report?.period) {
  currencyLabel(report?.currency);
  const timestamps = ['start_utc', 'end_utc_exclusive', 'start_local', 'end_local_exclusive', 'generated_at'];
  if (!nonempty(report?.restaurant?.id) || typeof report?.restaurant?.name !== 'string'
    || report.restaurant.id !== restaurantId || report.period !== period
    || !Object.prototype.hasOwnProperty.call(PERIOD_LABELS, report.period)
    || !nonempty(report.timezone) || !['restaurant', 'company', 'default'].includes(report.timezone_source)
    || !timestamps.every(key => nonempty(report[key]) && Number.isFinite(Date.parse(report[key])))
    || !Array.isArray(report.sales) || !Array.isArray(report.top_items)
    || !report.sales.every(row => row && /^\d{4}-\d{2}-\d{2}$/.test(row.date)
      && ['gross_paid_sales', 'refunds', 'net_receipts', 'order_count'].every(key => numeric(row[key])))
    || !report.top_items.every(row => row && (row.item_id === null || typeof row.item_id === 'string')
      && typeof row.name === 'string' && numeric(row.units_sold) && numeric(row.gross_item_sales))) {
    throw new Error('The report response is incomplete or does not match the selected restaurant and period.');
  }
  return report;
}

export function createReportSnapshot(data, restaurantId, period) {
  validateReport(data, restaurantId, period);
  return Object.freeze({
    ...data,
    restaurant: Object.freeze({ ...data.restaurant }),
    sales: Object.freeze(data.sales.map(row => Object.freeze({ ...row }))),
    top_items: Object.freeze(data.top_items.map(row => Object.freeze({ ...row }))),
  });
}

export function reportInfoRows(report) {
  validateReport(report);
  return [
    ['Restaurant', report.restaurant.name],
    ['Period', PERIOD_LABELS[report.period]],
    ['Currency', report.currency],
    ['Timezone', report.timezone],
    ['Timezone source', report.timezone_source],
    ['Start UTC', report.start_utc],
    ['End UTC exclusive', report.end_utc_exclusive],
    ['Start local', report.start_local],
    ['End local exclusive', report.end_local_exclusive],
    ['Generated at', report.generated_at],
    ...Object.entries(METRIC_DEFINITIONS),
  ];
}

export function createReportWorkbook(report) {
  validateReport(report);
  const workbook = XLSX.utils.book_new();
  const info = XLSX.utils.aoa_to_sheet([['UniGate Sales Report', ''], ...reportInfoRows(report)]);
  const sales = XLSX.utils.aoa_to_sheet([
    ['Date', 'Gross Paid Sales', 'Refunds', 'Net Receipts', 'Order Count'],
    ...report.sales.map(row => [row.date, row.gross_paid_sales, row.refunds, row.net_receipts, row.order_count]),
  ]);
  const items = XLSX.utils.aoa_to_sheet([
    ['Item', 'Quantity Sold', 'Gross Item Sales'],
    ...report.top_items.map(row => [row.name, row.units_sold, row.gross_item_sales]),
  ]);
  const moneyFormat = reportCurrencyNumberFormat(report.currency);
  report.sales.forEach((_, index) => {
    ['B', 'C', 'D'].forEach(column => { sales[`${column}${index + 2}`].z = moneyFormat; });
    sales[`E${index + 2}`].z = '0';
  });
  report.top_items.forEach((_, index) => {
    items[`B${index + 2}`].z = '0.########';
    items[`C${index + 2}`].z = moneyFormat;
  });
  // Explicit text cells prevent names beginning with =, +, -, or @ becoming formulas.
  [info, sales, items].forEach(sheet => {
    Object.keys(sheet).filter(key => !key.startsWith('!')).forEach(key => {
      if (typeof sheet[key].v === 'string') { sheet[key].t = 's'; delete sheet[key].f; }
    });
  });
  info['!cols'] = [{ wch: 24 }, { wch: 95 }];
  sales['!cols'] = [{ wch: 14 }, { wch: 22 }, { wch: 22 }, { wch: 22 }, { wch: 16 }];
  items['!cols'] = [{ wch: 42 }, { wch: 18 }, { wch: 24 }];
  XLSX.utils.book_append_sheet(workbook, info, 'Report Info');
  XLSX.utils.book_append_sheet(workbook, sales, 'Sales');
  XLSX.utils.book_append_sheet(workbook, items, 'Top Items');
  return workbook;
}

export function createReportPDF(report) {
  validateReport(report);
  const doc = new jsPDF();
  doc.text('UniGate Sales Report', 14, 15);
  autoTable(doc, {
    startY: 22, theme: 'plain', styles: { fontSize: 9, overflow: 'linebreak' },
    columnStyles: { 0: { cellWidth: 38 } },
    body: reportInfoRows(report),
  });
  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 8,
    head: [['Date', 'Net Receipts', 'Order Count']],
    body: report.sales.map(row => [row.date, formatReportCurrency(row.net_receipts, report.currency), row.order_count]),
  });
  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 8,
    head: [['Item', 'Quantity Sold', 'Gross Item Sales']],
    body: report.top_items.map(row => [row.name, row.units_sold, formatReportCurrency(row.gross_item_sales, report.currency)]),
  });
  return doc;
}

export function exportReportExcel(report) {
  XLSX.writeFile(createReportWorkbook(report), 'unigate-reports.xlsx');
}

export function exportReportPDF(report) {
  createReportPDF(report).save('unigate-reports.pdf');
}
